// src/LabTest/GetPackageDetailsTailwind.js

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import TestRating from "./TestRating";
import TestDetails from "./TestDetails";
import { usePackageCartActions } from "../../hooks/usePackageCartActions";
import { fetchAllRatings } from "../../Redux/ratingSlice";
import { 
  fetchWishlist, 
  addToWishlist, 
  removeFromWishlist,
  addWishlistOptimistic,
  removeWishlistOptimistic,
} from "../../Redux/wishlistSlice";
import { openCartSlider } from "../../Redux/cartSliderSlice";
import {
  FaBoxOpen,
  FaCoins,
  FaTag,
  FaCheckCircle,
  FaStar,
  FaClock,
  FaFlask,
  FaHeart,
  FaRegHeart,
  FaShoppingCart,
  FaRupeeSign,
} from "react-icons/fa";

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');

export default function GetPackageDetailsTailwind({ id, onClose }) {
  const dispatch = useDispatch();
  const [packageData, setPackageData] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const hasFetched = useRef(false);

  const cart = useSelector((state) => state.cart.items || []);
  const ratings = useSelector((state) => state.rating.ratings || {});
  const ratingLoading = useSelector((state) => state.rating.loading);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);

  const { addPackage, removePackage, hasDifferentStore } = usePackageCartActions();

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
    return user?.userId || null;
  };

  const loadWishlist = async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await dispatch(fetchWishlist(userId)).unwrap();
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const isInWishlist = (productId, categoryId = 3) => {
    return wishlistItems.some(
      item => Number(item.productId) === Number(productId) && 
              Number(item.categoryId) === Number(categoryId)
    );
  };

  const toggleWishlist = async (pkg, e) => {
    e.stopPropagation();
    const userId = getUserId();
    
    if (!userId) {
      toast.warning('Please login to add to wishlist');
      return;
    }

    const productId = pkg.packageId;
    const categoryId = 3;
    const isWished = isInWishlist(productId, categoryId);

    if (isWished) {
      dispatch(removeWishlistOptimistic({ productId, categoryId }));
    } else {
      dispatch(addWishlistOptimistic({ productId, categoryId }));
    }

    try {
      if (isWished) {
        await dispatch(removeFromWishlist({ userId, productId, categoryId })).unwrap();
        toast.success('Removed from wishlist');
      } else {
        await dispatch(addToWishlist({ userId, productId, categoryId })).unwrap();
        toast.success('Added to wishlist');
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      if (isWished) {
        dispatch(addWishlistOptimistic({ productId, categoryId }));
      } else {
        dispatch(removeWishlistOptimistic({ productId, categoryId }));
      }
      toast.error('Failed to update wishlist');
    }
  };

  const getPackageRating = (packageId) => {
    const key = `${packageId}_3`;
    return ratings[key] || { average: 0, total: 0, ratings: [] };
  };

  const renderStars = (rating, size = 14) => {
    const stars = [];
    const roundedRating = Math.round(rating || 0);
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar
          key={i}
          size={size}
          className={`${
            i <= roundedRating
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300'
          }`}
        />
      );
    }
    return stars;
  };

  const isPackageAdded = (packageId) => {
    return cart.some(
      (item) =>
        Number(item.productId) === Number(packageId) &&
        item.categoryId === 3
    );
  };

  const handlePackageAction = (pkg, e) => {
    if (e) e.stopPropagation();
    
    if (isPackageAdded(pkg.packageId)) {
      removePackage(pkg);
    } else {
      if (hasDifferentStore(pkg.labId)) {
        const confirmReplace = window.confirm(
          'Your cart contains items from a different lab. Would you like to replace them?'
        );
        if (!confirmReplace) return;
      }
      addPackage(pkg);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    if (!id) return;

    hasFetched.current = true;

    const fetchPackage = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const res = await axios.get(
          `${API_BASE_URL}/MasterAPI/TestPackagesById?PackageId=${id}`
        );

        if (res.data?.data?.length > 0) {
          const pkg = res.data.data[0];

          let parsedTests = [];
          if (pkg.testPackageDetailsJson) {
            try {
              parsedTests = JSON.parse(pkg.testPackageDetailsJson);
            } catch {
              parsedTests = [];
            }
          }

          const packageInfo = {
            packageId: pkg.packageId,
            packageName: pkg.packageName,
            packagePrice: pkg.packagePrice,
            totalPrice: pkg.totalPrice,
            discountPrice: pkg.discountPrice,
            description: pkg.description || "No description available",
            labId: pkg.labId,
            testCount: parsedTests.length,
            isPopular: pkg.isPopular || false,
            isFasting: pkg.isFasting || false,
            labName: pkg.labName || "N/A",
            companyName: pkg.companyName || "N/A",
          };

          setPackageData(packageInfo);

          const testsWithId = parsedTests.map((test, idx) => ({
            ...test,
            id: test.TestID || test.testID || test.id || idx,
            testName: test.TestName || test.testName || "Unknown Test",
          }));

          setTests(testsWithId);

          await dispatch(fetchAllRatings([{ productId: pkg.packageId, categoryId: 3 }]));
          await loadWishlist();

        } else {
          setError("Package not found");
          setPackageData(null);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load package details");
        setPackageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();

    return () => {
      hasFetched.current = false;
    };
  }, [id, dispatch]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
        <div className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-white border-b p-4 flex justify-between items-center">
            <h4 className="flex items-center gap-2 font-semibold">
              <FaBoxOpen className="text-red-500" />
              Error
            </h4>
            <button onClick={onClose} className="text-2xl">×</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h3 className="text-lg font-semibold text-gray-800">Failed to Load</h3>
              <p className="text-sm text-gray-600 mt-2">{error}</p>
              <p className="text-xs text-gray-400 mt-4">Package ID: {id}</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!packageData) {
    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
        <div className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-white border-b p-4 flex justify-between items-center">
            <h4 className="flex items-center gap-2 font-semibold">
              <FaBoxOpen className="text-red-500" />
              Package Details
            </h4>
            <button onClick={onClose} className="text-2xl">×</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <div className="text-gray-400 text-5xl mb-4">📦</div>
              <h3 className="text-lg font-semibold text-gray-800">Package Not Found</h3>
              <p className="text-sm text-gray-600 mt-2">Package with ID {id} not found</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const added = isPackageAdded(packageData.packageId);
  const inWishlist = isInWishlist(packageData.packageId, 3);
  const { average: avgRating, total: totalReviews } = getPackageRating(packageData.packageId);
  
  const originalPrice = Number(packageData.packagePrice) || 0;
  const discountPrice = Number(packageData.discountPrice) || originalPrice;
  const totalValue = Number(packageData.totalPrice) || originalPrice;
  const discountAmount = totalValue - discountPrice;
  const discountPercent = totalValue > 0 ? Math.round((discountAmount / totalValue) * 100) : 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      <div
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-gray-50 z-50 shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="bg-white border-b p-4 flex justify-between items-center">
          <h4 className="flex items-center gap-2 font-semibold">
            <FaBoxOpen className="text-red-500" />
            Package Details
          </h4>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          
          {/* Package Name */}
          <div className="bg-white p-4 rounded-xl border relative">
            <button
              onClick={(e) => toggleWishlist(packageData, e)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur-sm shadow-md hover:bg-white transition-all duration-200"
              disabled={wishlistLoading}
            >
              {wishlistLoading ? (
                <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                inWishlist ? (
                  <FaHeart size={18} className="text-red-500 fill-red-500" />
                ) : (
                  <FaRegHeart size={18} className="text-gray-400 hover:text-red-500" />
                )
              )}
            </button>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-md">
                <FaBoxOpen size={22} className="text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-lg font-bold text-gray-800 pr-8">
                  {packageData.packageName}
                </h5>
                {packageData.labName && packageData.labName !== "N/A" && (
                  <p className="text-xs text-gray-500 mt-1">
                    🏥 {packageData.labName}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="bg-white p-4 rounded-xl border">
            <div className="flex flex-wrap gap-2">
              {packageData.isPopular && (
                <span className="bg-yellow-100 text-yellow-700 text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                  <FaStar size={12} /> Popular
                </span>
              )}
              {packageData.isFasting && (
                <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                  <FaClock size={12} /> Fasting Required
                </span>
              )}
              <span className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full font-medium">
                ✅ Available
              </span>
            </div>
          </div>

          {/* Description */}
          {packageData.description && (
            <div className="bg-white p-4 rounded-xl border">
              <h6 className="font-semibold mb-2">Description</h6>
              <p className="text-sm text-gray-600">
                {packageData.description}
              </p>
            </div>
          )}

          {/* Package Information */}
          <div className="bg-white p-4 rounded-xl border">
            <h6 className="font-semibold mb-2">Package Information</h6>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total Tests</span>
                <span className="font-medium">{packageData.testCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">🏥 Lab</span>
                <span className="font-medium">{packageData.labName || "N/A"}</span>
              </div>
              {packageData.companyName && packageData.companyName !== "N/A" && (
                <div className="flex justify-between">
                  <span className="text-gray-500">🏢 Company</span>
                  <span className="font-medium">{packageData.companyName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Price Details */}
          <div className="bg-white p-4 rounded-xl border">
            <h6 className="font-semibold mb-3 text-gray-700">💰 Price Details</h6>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <span className="text-gray-500 flex items-center gap-1">
                  <FaTag size={14} className="text-gray-400" /> Total Value
                </span>
                <span className="font-medium text-gray-400 line-through">
                  ₹{totalValue.toLocaleString()}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-1 border-b border-gray-50">
                <span className="text-gray-500 flex items-center gap-1">
                  <FaCoins size={14} className="text-green-500" /> Discount Price
                </span>
                <span className="font-bold text-green-600 text-lg">
                  ₹{discountPrice.toLocaleString()}
                </span>
              </div>

              {discountAmount > 0 && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-500 flex items-center gap-1">
                    <FaCheckCircle size={14} className="text-red-500" /> You Save
                  </span>
                  <span className="font-bold text-red-500">
                    ₹{discountAmount.toLocaleString()} ({discountPercent}% OFF)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 🔁 MOVED: Tests List - Now Above Rating */}
          {tests.length > 0 && (
            <div className="bg-white p-4 rounded-xl border">
              <h6 className="font-semibold mb-3 flex items-center gap-2">
                <FaFlask className="text-red-500" />
                Included Tests ({tests.length})
              </h6>

              <div className="space-y-1 max-h-[300px] overflow-y-auto">
                {tests.map((test, idx) => {
                  const testId = test.id || test.TestID;
                  
                  return (
                    <div
                      key={idx}
                      className="p-2 hover:bg-gray-50 rounded-lg transition cursor-pointer border-b border-gray-50 last:border-b-0"
                      onClick={() => {
                        if (testId) {
                          setSelectedTestId(testId);
                        }
                      }}
                    >
                      <p className="text-sm font-medium text-gray-700">
                        {test.testName}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 🔁 MOVED: Rating Section - Now Below Tests */}
          <div className="bg-white p-4 rounded-xl border">
            <h6 className="font-semibold mb-2 text-gray-700">⭐ Rating & Reviews</h6>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex">
                {renderStars(avgRating, 18)}
              </div>
              {!ratingLoading && totalReviews > 0 && (
                <span className="text-sm text-gray-500">
                  ({totalReviews} reviews)
                </span>
              )}
            </div>
            <TestRating 
              productId={packageData.packageId} 
              categoryId={3}
              showLabel={true}
              size="sm"
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="border-t bg-white p-4">
          <div className="flex flex-col mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-green-600">
                ₹{discountPrice.toLocaleString()}
              </span>
              {discountAmount > 0 && (
                <span className="text-xs line-through text-gray-400">
                  ₹{totalValue.toLocaleString()}
                </span>
              )}
            </div>
            {discountAmount > 0 && (
              <span className="text-xs text-red-500 font-semibold">
                Save ₹{discountAmount.toLocaleString()} ({discountPercent}% off)
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handlePackageAction(packageData)}
              className={`flex-1 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition-all duration-200 ${
                added
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              <FaShoppingCart />
              {added ? "Remove" : "Add"}
            </button>

            <button
              onClick={() => {
                if (onClose) onClose();
                setTimeout(() => {
                  dispatch(openCartSlider());
                }, 100);
              }}
              className="flex-1 py-2.5 border-2 border-green-600 text-green-600 rounded-lg font-bold hover:bg-green-50 transition-all duration-200"
            >
              View Cart
            </button>
          </div>
        </div>
      </div>

      {selectedTestId && (
        <TestDetails 
          id={selectedTestId} 
          onClose={() => setSelectedTestId(null)} 
        />
      )}
    </>
  );
}